import { makeAutoObservable, action, computed } from 'mobx';
import { HubConnectionState } from '@microsoft/signalr';
import { ensureConnected, setConnectionStateListener } from '@/api/signalr';
import type { RootStore } from './RootStore';

export type FlashKind = 'accepted' | 'rejected';
export type FlashState = { id: number; kind: FlashKind; score: number; reason?: string } | null;
export type ConnectionBanner = 'offline' | 'reconnecting' | 'restored' | null;

let flashSeq = 0;

export class UiStore {
  theme: 'light' | 'dark' = 'dark';
  flashState: FlashState = null;
  connectionState: HubConnectionState = HubConnectionState.Disconnected;
  // true once we've had at least one real loss (Reconnecting or Disconnected)
  private _hadLoss = false;
  // set to 'restored' transiently; null when timer fires
  private _restoredOverride: ConnectionBanner = null;
  private _restoredTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(_root: RootStore) {
    makeAutoObservable(this, { connectionBanner: computed }, { autoBind: true });
    setConnectionStateListener(action((s: HubConnectionState) => {
      this.connectionState = s;

      if (s === HubConnectionState.Reconnecting || s === HubConnectionState.Disconnected) {
        this._hadLoss = true;
        this._clearRestoredTimer();
        this._restoredOverride = null;
      }

      if (s === HubConnectionState.Connected && this._hadLoss) {
        this._clearRestoredTimer();
        this._restoredOverride = 'restored';
        this._restoredTimer = setTimeout(action(() => {
          this._restoredOverride = null;
        }), 1600);
      }
    }));
  }

  get connectionBanner(): ConnectionBanner {
    if (this._restoredOverride === 'restored') return 'restored';
    switch (this.connectionState) {
      case HubConnectionState.Reconnecting:
      case HubConnectionState.Connecting:
        return this._hadLoss ? 'reconnecting' : null;
      case HubConnectionState.Disconnected:
        return this._hadLoss ? 'offline' : null;
      default:
        return null;
    }
  }

  private _clearRestoredTimer() {
    if (this._restoredTimer !== null) {
      clearTimeout(this._restoredTimer);
      this._restoredTimer = null;
    }
  }

  flash(kind: FlashKind, score: number = 0, reason?: string) {
    this.flashState = { id: ++flashSeq, kind, score, reason };
    setTimeout(action(() => { this.flashState = null; }), 1500);
  }

  async requestReconnect() {
    await ensureConnected();
  }
}
